import React from 'react';
import { Avatar, rem } from '@mantine/core';
import { SpotlightActionData } from '@mantine/spotlight';
import {
  IconSearch,
  IconUser,
  IconCalendarEventFilled,
  IconMapPin,
  IconFileText,
  IconListTree,
  IconPalette,
} from '@tabler/icons-react';

// Types
export interface SearchResult {
  id: string;
  type:
    | 'artist'
    | 'organizer'
    | 'event'
    | 'upcoming_event'
    | 'past_event'
    | 'dance_style'
    | 'event_type'
    | 'post'
    | 'area';
  title: string;
  description: string;
  url: string;
  avatar_url?: string; // This holds the URL for artist avatars or organization images
}

export interface SearchResponse {
  results: SearchResult[];
  total_count: number;
  query: string;
}

// Constants
export const SEARCH_DEBOUNCE_TIME = 300;
export const SPOTLIGHT_RESULTS_LIMIT = 6;
export const SEARCH_API_ENDPOINT = '/api/search';

export const SEARCH_RESULT_GROUPS = {
  DANCE_STYLES: 'Dance Styles',
  CITIES: 'Cities',
  ARTISTS: 'Artists',
  EVENT_TYPES: 'Event Types',
  ORGANIZERS: 'Organizers',
  POSTS: 'Posts',
  EVENTS: 'Events',
  OTHER: 'Other',
} as const;

// Group ordering for display (Updated to match new backend logic)
export const SEARCH_GROUP_ORDER = [
  SEARCH_RESULT_GROUPS.DANCE_STYLES,
  SEARCH_RESULT_GROUPS.CITIES,
  SEARCH_RESULT_GROUPS.ARTISTS,
  SEARCH_RESULT_GROUPS.EVENT_TYPES,
  SEARCH_RESULT_GROUPS.ORGANIZERS,
  SEARCH_RESULT_GROUPS.POSTS,
  SEARCH_RESULT_GROUPS.EVENTS,
];

// Icon generation utilities
export const getIconForType = (type: SearchResult['type'], size: number = 24) => {
  const iconStyle = { width: rem(size), height: rem(size) };
  const iconColor = '#EA580C';

  switch (type) {
    case 'dance_style':
      return <IconPalette style={iconStyle} color={iconColor} />;
    case 'area':
      return <IconMapPin style={iconStyle} color={iconColor} />;
    case 'artist':
      return <IconUser style={iconStyle} color={iconColor} />;
    case 'organizer':
      return <IconListTree style={iconStyle} color={iconColor} />;
    case 'event_type':
      return <IconCalendarEventFilled style={iconStyle} color={iconColor} />;
    case 'post':
      return <IconFileText style={iconStyle} color={iconColor} />;
    case 'event':
    case 'upcoming_event':
    case 'past_event':
      return <IconCalendarEventFilled style={iconStyle} color={iconColor} />;
    default:
      return <IconSearch style={iconStyle} color={iconColor} />; // Corrected default
  }
};

// Group name mapping
export const getGroupName = (type: SearchResult['type']): string => {
  switch (type) {
    case 'dance_style':
      return SEARCH_RESULT_GROUPS.DANCE_STYLES;
    case 'area':
      return SEARCH_RESULT_GROUPS.CITIES;
    case 'artist':
      return SEARCH_RESULT_GROUPS.ARTISTS;
    case 'event_type':
      return SEARCH_RESULT_GROUPS.EVENT_TYPES;
    case 'organizer':
      return SEARCH_RESULT_GROUPS.ORGANIZERS;
    case 'post':
      return SEARCH_RESULT_GROUPS.POSTS;
    case 'event':
    case 'upcoming_event':
    case 'past_event':
      return SEARCH_RESULT_GROUPS.EVENTS;
    default:
      return SEARCH_RESULT_GROUPS.OTHER;
  }
};

// Convert search results to Spotlight actions
export const convertToSpotlightActions = (results: SearchResult[]): SpotlightActionData[] => {
  return results.slice(0, SPOTLIGHT_RESULTS_LIMIT).map((result) => ({
    id: result.id,
    label: result.title,
    description: result.type === 'upcoming_event' ? `🟠 ${result.description}` : result.description,
    onClick: () => (window.location.href = result.url),
    leftSection: getSearchResultIcon(result),
    group: getGroupName(result.type),
  }));
};

// Get icon for search result (handles avatars for artists and organizers)
export const getSearchResultIcon = (result: SearchResult, size: number = 48) => {
  if ((result.type === 'artist' || result.type === 'organizer') && result.avatar_url) {
    return <Avatar src={result.avatar_url} size={size} radius="xl" />;
  }
  return getIconForType(result.type, size);
};

// Create "See all results" action
export const createSeeAllResultsAction = (query: string, onShowAll: () => void): SpotlightActionData => ({
  id: 'see-all-results',
  label: `See all results for "${query}"`,
  description: 'View complete search results',
  onClick: onShowAll,
  leftSection: getIconForType('post'),
  group: 'More Results',
});

// Filter and group results for display
export interface GroupedResults {
  [groupName: string]: SearchResult[];
}

export const groupResultsByType = (results: SearchResult[]): GroupedResults => {
  const grouped: GroupedResults = {};

  results.forEach((result) => {
    const groupName = getGroupName(result.type);
    if (!grouped[groupName]) {
      grouped[groupName] = [];
    }
    grouped[groupName].push(result);
  });

  return grouped;
};

// Get ordered groups for consistent display
export const getOrderedGroups = (
  groupedResults: GroupedResults,
): Array<{
  name: string;
  results: SearchResult[];
}> => {
  return SEARCH_GROUP_ORDER.map((groupName) => ({
    name: groupName,
    results: groupedResults[groupName] || [],
  })).filter((group) => group.results.length > 0);
};

// Color helpers for event types
export const getEventDescriptionColor = (type: SearchResult['type']): string => {
  return type === 'upcoming_event' ? '#EA580C50' : 'dimmed';
};

// API utility
export const searchAPI = async (query: string): Promise<SearchResponse> => {
  try {
    const response = await fetch(`${SEARCH_API_ENDPOINT}?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    });

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Search error:', error);
    return { results: [], total_count: 0, query };
  }
};

// Validation helpers
export const isValidSearchQuery = (query: string): boolean => {
  return query.trim().length > 0;
};

export const shouldShowSeeAllAction = (query: string, hasResults: boolean): boolean => {
  return isValidSearchQuery(query) && hasResults;
};
